import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, HStack, Spacer, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import deepEqual from 'fast-deep-equal';
import { useCallback } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { selectCurrencyPairPrecision } from '@/api/config/selectors.ts';
import { useRecordPriceMutation } from '@/api/mifid/index.ts';
import { TimeProgress } from '@/App/tiles/shared/TimeProgress.tsx';
import { SgButton } from '@/components/sg-design-system/components/input/SgButton';
import { checkIfUserInternalSales, useUserContext } from '@/context/UserContext.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import { getIsStreamingConnected, getSignalRConnectionId } from '@/store/streaming/slice';
import { useCancelSwapRfsMutation, useRequestSwapRfsMutation } from '@/store/swap/api/index.ts';
import { getRequestSwapRfsMutationCacheKey, selectSwapPrices } from '@/store/swap/selectors/rfs.ts';
import { selectSwapIsPriceable, selectSwapTile } from '@/store/swap/selectors/tile.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { isIsoDate } from '@/utils/date.ts';
import { getNewRfsId } from '@/utils/rfs/getNewRfsId.ts';

export type SwapFooterProps = {
  tileId: string;
};

export const SwapFooter = (props: SwapFooterProps) => {
  const { tileId } = props;

  const { formatDate, formatTime } = useIntl();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const isSignalRConnected = useAppSelector(getIsStreamingConnected);

  const userContextValue = useUserContext();
  const isInternalSales = checkIfUserInternalSales(userContextValue);

  const {
    rfsId,
    signalRConnectionId,
    clientId,

    type,

    currencyPair,
    currency1,
    currency2,
    currencyPairPrecision,

    notionalCurrencyIndex,

    nearNotional,
    nearExpiryDate,
    nearPrice,

    farNotional,
    farExpiryDate,
    farPrice,

    isRfsPending,
    isPriceable,
    canRecordRates,
    rfsRemainingTime,
    totalTime,
    traderName,

    nearFixingDate,
    farFixingDate,
    xCurrency,
    fixingSource,
    fixingCurrency,
    sndFixingSource,
    quoteId,
    rates,
    negociations,
    rfsStartDate,
  } = useAppSelector((state) => {
    const tile = selectSwapTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        rfsId: undefined,
        signalRConnectionId: undefined,
        clientId: undefined,
        type: undefined,
        currencyPair: undefined,
        currency1: undefined,
        currency2: undefined,
        notionalCurrencyIndex: undefined,
        nearNotional: undefined,
        nearExpiryDate: undefined,
        nearPrice: undefined,
        farNotional: undefined,
        farExpiryDate: undefined,
        farPrice: undefined,
        rfsRemainingTime: undefined,
        totalTime: undefined,
        traderName: undefined,
        nearFixingDate: undefined,
        farFixingDate: undefined,
        xCurrency: undefined,
        fixingSource: undefined,
        fixingCurrency: undefined,
        sndFixingSource: undefined,
        isRfsPending: false,
        isPriceable: false,
        canRecordRates: false,
        currencyPairPrecision: 0,
        quoteId: undefined,
        rates: undefined,
        negociations: undefined,
        rfsStartDate: undefined,
      };
    }

    const type = tile.isNonDeliverable
      ? ('FxNdSwap' as const)
      : tile.properties.type !== 'historical'
        ? ('FxSwap' as const)
        : isDefined(tile.properties.nearPrice)
          ? ('FxRollover' as const)
          : ('FxPredeliver' as const);

    const clientId = selectTileClientId(state, tileId);

    const streamedRfs = tile.properties.rfs?.status === 'STREAMED' ? tile.properties.rfs : undefined;

    const prices = selectSwapPrices(state, tileId);

    return {
      rfsId: tile.properties.rfs?.rfsId,
      clientId,

      type,

      currencyPair: tile.properties.currencyPair,

      currency1: tile.properties.currency1,
      currency2: tile.properties.currency2,

      currencyPairPrecision: selectCurrencyPairPrecision(state, tile.properties.currencyPair),

      notionalCurrencyIndex: tile.properties.nearNotionalCurrencyIndex,

      nearNotional: tile.properties.nearNotional,
      nearExpiryDate: tile.properties.nearExpiryDate,
      nearPrice: tile.properties.nearPrice?.bid,

      farNotional: tile.properties.farNotional,
      farExpiryDate: tile.properties.farExpiryDate,
      farPrice: tile.isNonDeliverable ? undefined : tile.properties.farPrice?.bid,

      isRfsPending: tile.properties.rfs?.status === 'PENDING',
      isPriceable: selectSwapIsPriceable(state, tileId),
      canRecordRates: true,

      rfsRemainingTime: streamedRfs?.quote.rfsRemainingTime,

      totalTime: streamedRfs?.quote.rfsWindow,
      traderName: streamedRfs?.quote.traderId,
      signalRConnectionId: getSignalRConnectionId(state),

      // NDS
      isNonDeliverable: tile.isNonDeliverable,
      ...(tile.isNonDeliverable
        ? {
            nearFixingDate: tile.properties.nearFixingDate,
            farFixingDate: tile.properties.farFixingDate,

            fixingCurrency: tile.properties.fixingCurrency,
            xCurrency: tile.properties.xCurrency,
            fixingSource: tile.properties.fixingSource,
            sndFixingSource: tile.properties.sndFixingSource,
          }
        : {}),

      quoteId: tile.properties.rfs?.status === 'STREAMED' ? tile.properties.rfs.quote.quoteId : undefined,
      rates: prices?.rates,
      negociations: tile.properties.rfs?.status === 'STREAMED' ? [...tile.properties.rfs.negociations].reverse() : [],
      rfsStartDate: tile.properties.rfs?.status === 'STREAMED' ? tile.properties.rfs.requestedAt : undefined,
    };
  }, deepEqual);

  const [requestSwapRfs, { isLoading: isRfsRequestLoading }] = useRequestSwapRfsMutation({
    fixedCacheKey: getRequestSwapRfsMutationCacheKey(tileId),
  });

  const [requestSwapRfsCancelation] = useCancelSwapRfsMutation();

  const handleRequestRfs = async () => {
    if (
      isNotDefined(clientId) ||
      isNotDefined(type) ||
      isNotDefined(currency1) ||
      isNotDefined(currency2) ||
      isNotDefined(notionalCurrencyIndex) ||
      isNotDefined(nearNotional) ||
      isNotDefined(nearExpiryDate) ||
      !isIsoDate(nearExpiryDate) ||
      isNotDefined(farNotional) ||
      isNotDefined(farExpiryDate) ||
      !isIsoDate(farExpiryDate) ||
      !isPriceable ||
      isNotDefined(signalRConnectionId)
    ) {
      return;
    }

    const newRfsId = getNewRfsId();

    sgwtWebAnalytics?.trackEvent('Swap', 'RFS requested');

    await requestSwapRfs({
      type,
      tileId,
      rfsId: newRfsId,
      requestedAt: Date.now(),
      signalRConnectionId,
      counterPartyId: clientId,

      currency1,
      currency2,
      currencyPairPrecision,

      notionalCurrencyIndex,

      xCurrency,
      fixingSource,
      fixingCurrency,
      sndFixingSource,

      nearNotional,
      nearExpiryDate,
      nearPrice,
      nearFixingDate,

      farNotional,
      farExpiryDate,
      farPrice,
      farFixingDate,
    });
  };

  const cancelRfs = useCallback(() => {
    if (isNotDefined(rfsId)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('Swap', 'RFS canceled');

    requestSwapRfsCancelation({ tileId, rfsId });
  }, [rfsId, tileId, requestSwapRfsCancelation, sgwtWebAnalytics]);

  const [recordPrice, { isLoading: isRecordRatesPending }] = useRecordPriceMutation();

  const handleRecordRates = () => {
    sgwtWebAnalytics?.trackEvent('Swap', 'RFS price recorded');

    if (isNotDefined(rfsId) || isNotDefined(quoteId) || isNotDefined(rates?.bid) || isNotDefined(rates?.ask)) {
      return;
    }

    recordPrice({
      tileId,
      date: Date.now(),

      quoteId,
      rfsId,
      prices: [
        {
          bid: rates.bid,
          ask: rates.ask,
        },
      ],
    });
  };

  if (isNotDefined(rfsRemainingTime) && !isRfsPending) {
    return (
      <div className="p-2">
        <SgButton
          tabIndex={-1}
          type="button"
          size="sm"
          purpose="primary"
          color="info"
          onClick={handleRequestRfs}
          disabled={isRecordRatesPending || !isPriceable || !isSignalRConnected || isRfsRequestLoading || isRfsPending}
          block
          data-e2e="price-rfs-button"
        >
          <FormattedMessage id="CashTile.footer.Price" />
        </SgButton>
      </div>
    );
  }

  return (
    <VStack className="px-2 pt-3" gap="8px">
      <TimeProgress totalTime={totalTime} remainingTime={rfsRemainingTime} onCancel={cancelRfs} data-e2e="rfs" />

      {isInternalSales && (
        <HStack fullWidth gap="8px">
          <Button
            iconPosition="start"
            purpose="primary"
            color="danger"
            size="sm"
            onClick={handleRecordRates}
            disabled={!canRecordRates}
            data-e2e="timestamp-button"
          >
            <i className="icon">timer</i>
            Timestamp
          </Button>

          {isDefined(rfsStartDate) && isDefined(negociations) && (
            <OverlayTrigger
              overlay={
                <Popover style={{ maxWidth: '300px' }}>
                  <Popover.Header data-e2e="timestamp-popover-title">
                    <FormattedMessage id="cash.forward.negociation.popover.header" values={{ currencyPair }} />
                  </Popover.Header>

                  <Popover.Body>
                    <VStack className="mb-8px">
                      <div className="mb-8px">
                        <FormattedMessage
                          id="cash.forward.negociation.popover.initialisation"
                          values={{
                            initialisationDate: `${formatDate(rfsStartDate, { dateStyle: 'long' })} ${formatTime(rfsStartDate, { timeStyle: 'short' })}`,
                          }}
                        />
                      </div>

                      <div data-e2e="timestamp-popover-body">
                        {negociations.map((negociation, negociationIndex) => (
                          // oxlint-disable-next-line no-array-index-key -- the index is really the key
                          <div key={negociationIndex} className="text-nowrap">
                            <FormattedMessage
                              id="cash.forward.negociation.popover.item"
                              values={{
                                index: negociations.length - negociationIndex,
                                date: `${formatDate(negociation.date, { dateStyle: 'medium' })} ${formatTime(negociation.date, { timeStyle: 'short' })}`,
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </VStack>
                  </Popover.Body>
                </Popover>
              }
              placement="right"
              trigger="click"
            >
              <span data-e2e="timestamp-count" className="border badge rounded-pill badge-discreet-secondary fs-12">
                {negociations?.length ?? 0}
              </span>
            </OverlayTrigger>
          )}

          <Spacer />

          {isDefined(traderName) && (
            <Button size="sm" data-e2e="trader-name-button">
              {traderName}
            </Button>
          )}
        </HStack>
      )}
    </VStack>
  );
};
